// ============================================================
// src/components/sms/SMSConversationPanel.tsx
// Changements :
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs,
  onSnapshot, doc, updateDoc, where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { invokeFunctionSilent } from '@/lib/cloudrun';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  MessageSquare, Send, Phone, Search,
  Circle, CheckCheck, AlertCircle, Loader2, RefreshCw, User,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

interface SMSThread {
  phone: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  direction: string;
}

interface SMSMessage {
  id: string;
  from_number: string;
  to_number: string;
  body: string;
  direction: string;
  status: string;
  is_read: boolean;
  created_at: string;
}

// Normalise les données Firestore (camelCase) → interface attendue
function normalizeMsg(id: string, data: any): SMSMessage {
  return {
    id,
    from_number: data.fromNumber || data.from_number || '',
    to_number: data.toNumber || data.to_number || '',
    body: data.body || '',
    direction: data.direction || 'outbound',
    status: data.status || 'sent',
    is_read: data.isRead ?? data.is_read ?? false,
    created_at: data.createdAt?.toDate?.()?.toISOString() || data.created_at || new Date().toISOString(),
  };
}

export function SMSConversationPanel() {
  const { user } = useAuth();
  const [threads, setThreads] = useState<SMSThread[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<SMSMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ── Charger les fils SMS ──────────────────────────────────────

  const loadThreads = useCallback(async () => {
    try {
      const snap = await getDocs(
        query(collection(db, 'smsMessages'), orderBy('createdAt', 'desc'))
      );

      const threadMap = new Map<string, SMSThread>();
      snap.docs.forEach(d => {
        const msg = normalizeMsg(d.id, d.data());
        const externalPhone = msg.direction === 'inbound' ? msg.from_number : msg.to_number;
        const existing = threadMap.get(externalPhone);
        if (!existing) {
          threadMap.set(externalPhone, {
            phone: externalPhone,
            lastMessage: msg.body,
            lastMessageAt: msg.created_at,
            unreadCount: msg.direction === 'inbound' && !msg.is_read ? 1 : 0,
            direction: msg.direction,
          });
        } else if (msg.direction === 'inbound' && !msg.is_read) {
          existing.unreadCount++;
        }
      });

      setThreads(Array.from(threadMap.values()));
    } catch (err) {
      console.error('Error loading SMS threads:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Charger les messages d'un fil ────────────────────────────

  const loadMessages = useCallback(async (phone: string) => {
    try {
      const snap = await getDocs(
        query(collection(db, 'smsMessages'), orderBy('createdAt', 'asc'))
      );

      const msgs = snap.docs
        .map(d => normalizeMsg(d.id, d.data()))
        .filter(m => m.from_number === phone || m.to_number === phone);

      setMessages(msgs);

      // Marquer les messages entrants comme lus
      const unreadSnap = await getDocs(
        query(
          collection(db, 'smsMessages'),
          where('fromNumber', '==', phone),
          where('direction', '==', 'inbound'),
          where('isRead', '==', false)
        )
      );
      unreadSnap.docs.forEach(d => updateDoc(d.ref, { isRead: true }));

      setThreads(prev => prev.map(t => t.phone === phone ? { ...t, unreadCount: 0 } : t));
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (selectedPhone) loadMessages(selectedPhone);
  }, [selectedPhone, loadMessages]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // onSnapshot Firestore = realtime natif, aucun canal à gérer

  useEffect(() => {
    const q = query(collection(db, 'smsMessages'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, () => {
      loadThreads();
      if (selectedPhone) loadMessages(selectedPhone);
    });
    return () => unsub();
  }, [selectedPhone, loadThreads, loadMessages]);

  // ── Envoyer un SMS ───────────────────────────────────────────

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedPhone || sending) return;
    setSending(true);
    try {
      await invokeFunctionSilent('send-sms-notification', {
        to: selectedPhone,
        message: newMessage.trim(),
      });
      setNewMessage('');
      toast.success('SMS envoyé');
    } catch (err) {
      console.error('Error sending SMS:', err);
      toast.error("Erreur lors de l'envoi du SMS.");
    } finally {
      setSending(false);
    }
  };

  const filteredThreads = threads.filter(t =>
    t.phone.includes(searchQuery) ||
    t.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatPhone = (phone: string) => {
    if (phone.length === 12 && phone.startsWith('+1'))
      return `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
    return phone;
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckCheck className="w-3 h-3 text-emerald-400" />;
      case 'sent': return <CheckCheck className="w-3 h-3 text-muted-foreground" />;
      case 'failed': return <AlertCircle className="w-3 h-3 text-destructive" />;
      default: return <Circle className="w-3 h-3 text-muted-foreground" />;
    }
  };

  return (
    <div className="flex h-[calc(100vh-12rem)] rounded-xl border border-border overflow-hidden bg-card">
      {/* Thread list */}
      <div className="w-80 border-r border-border flex flex-col">
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Rechercher..." value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" />
          </div>
        </div>
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center p-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : filteredThreads.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
              <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">Aucun message SMS</p>
              <p className="text-xs mt-1">Les messages entrants apparaîtront ici</p>
            </div>
          ) : filteredThreads.map(thread => (
            <button key={thread.phone} onClick={() => setSelectedPhone(thread.phone)}
              className={`w-full text-left p-3 border-b border-border/50 hover:bg-muted/50 transition-colors ${selectedPhone === thread.phone ? 'bg-primary/10' : ''}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium truncate">{formatPhone(thread.phone)}</span>
                    {thread.unreadCount > 0 && (
                      <Badge variant="destructive" className="text-[10px] h-5 min-w-5 px-1.5">{thread.unreadCount}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {thread.direction === 'inbound' ? '← ' : '→ '}{thread.lastMessage}
                  </p>
                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                    {format(new Date(thread.lastMessageAt), 'dd MMM HH:mm', { locale: fr })}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </ScrollArea>
        <div className="p-2 border-t border-border">
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={loadThreads}>
            <RefreshCw className="w-3 h-3 mr-1" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Conversation area */}
      <div className="flex-1 flex flex-col">
        {!selectedPhone ? (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <Phone className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">Sélectionnez une conversation</p>
            <p className="text-xs mt-1">Choisissez un contact pour voir les messages</p>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-border flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">{formatPhone(selectedPhone)}</p>
                <p className="text-[10px] text-muted-foreground">{messages.length} message{messages.length > 1 ? 's' : ''}</p>
              </div>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {messages.map(msg => {
                  const isOutbound = msg.direction === 'outbound';
                  return (
                    <div key={msg.id} className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${isOutbound ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted text-foreground rounded-bl-md'}`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                        <div className={`flex items-center gap-1 mt-1 ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                          <span className={`text-[10px] ${isOutbound ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                            {format(new Date(msg.created_at), 'HH:mm')}
                          </span>
                          {isOutbound && statusIcon(msg.status)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <Textarea value={newMessage} onChange={e => setNewMessage(e.target.value)}
                  placeholder="Écrire un message..." className="resize-none min-h-[40px] max-h-[120px] text-sm" rows={1}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} />
                <Button size="icon" onClick={handleSend} disabled={!newMessage.trim() || sending} className="h-10 w-10 flex-shrink-0">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}